import React from 'react'
import { array, number, string } from 'prop-types'
import styled from '@emotion/styled'

// Import Components
import { ChevronBlock } from '../svgs/ChevronBlock'

// custom hooks
import { useCarousel } from '@jelly/utils'

// Styled Components via Emotion
const CarouselSlotWrapper = styled.div`
	display: flex;
	flex-direction: row;
	margin: 0 auto 30px auto;
	overflow: hidden;
	width: 100%;
`
const CarouselSlot = styled.div`
	flex: 1 0 100%; /* For mobile, always have only one card/item visible at a time */
	font-size: 16px;
	order: ${(props) => props.order};
	text-align: center;
	transition: ${({ isSliding }) => (isSliding ? 'none' : 'transform .5s ease')};
	width: 100%;

	transform: ${({ direction, isSliding }) => {
		if (!isSliding) return 'translateX(calc(0%))'
		if (direction === 'previous') return 'translateX(calc(-100%))'
		return 'translateX(100%)'
	}};

	${({ theme }) => theme.MQ.M} {
		flex: ${({ numItemsToDisplay }) =>
			numItemsToDisplay === 1 ? '1 0 100%' : `1 0 ${parseInt(100 / numItemsToDisplay, 10)}%`};
		font-size: 19px;
	}
`
const CarouselWrapper = styled.div`
	align-items: center;
	display: flex;
	justify-content: space-between;
	margin: auto;
`
const Button = `
    cursor: pointer;
    height: 50px;
    opacity: .7;
    padding-left: 30px;
    transition: all .4s ease;
    width: 50px;

    &:hover {
        opacity: 1;
    }
`
const PreviousButton = styled.div`
	${Button}
	display: none;
	transform: rotate(180deg);

	${({ theme }) => theme.MQ.S} {
		display: block;
	}
`
const NextButton = styled.div`
	${Button}
	display: none;

	${({ theme }) => theme.MQ.S} {
		display: block;
	}
`
const DotsWrapper = styled.div`
	margin: auto;
	text-align: center;
	width: 100%;
`
const Dot = styled.div`
	cursor: pointer;
	background: ${({ fillDot, fillDotColor }) => (fillDot ? (fillDotColor ? fillDotColor : '#FFF') : '')};
	border: 2px solid #fff;
	border-color: ${({ borderColor }) => borderColor || '#FFF'};
	border-radius: 50%;
	display: inline-block;
	height: 10px;
	margin: 0 15px;
	width: 10px;

	${({ theme }) => theme.MQ.M} {
		height: 15px;
		width: 15px;
	}
`

Carousel.propTypes = {
	borderColor: string,
	elements: array.isRequired,
	fillDotColor: string,
	numItemsToDisplay: number,
}

/**
 * This is the Carousel Component
 * Can use this functionality for any Carousel
 *
 * Future Improvements:
 * Refactor this component so we can just use the functionality and pass in any type of card component
 */
export function Carousel({ elements, numItemsToDisplay = 1, fillDotColor, borderColor }) {
	const {
		carouselState: { position, shouldSlide, direction },
		handlers,
		getOrder,
		previousSlide,
		nextSlide,
		jumpToSlide,
	} = useCarousel({ elements })

	return (
		<>
			<CarouselWrapper {...handlers}>
				<PreviousButton onClick={previousSlide}>
					<ChevronBlock />
				</PreviousButton>
				<CarouselSlotWrapper>
					{elements.map((elementCard, i) => {
						return (
							<CarouselSlot
								direction={direction}
								isSliding={shouldSlide}
								order={getOrder(i)}
								numItemsToDisplay={numItemsToDisplay}
								key={i}
							>
								{elementCard}
							</CarouselSlot>
						)
					})}
				</CarouselSlotWrapper>
				<NextButton onClick={nextSlide}>
					<ChevronBlock />
				</NextButton>
			</CarouselWrapper>
			<DotsWrapper>
				{elements.map((element, i) => {
					return (
						<Dot
							fillDot={position === i}
							key={i}
							onClick={() => jumpToSlide(i)}
							fillDotColor={fillDotColor}
							borderColor={borderColor}
						/>
					)
				})}
			</DotsWrapper>
		</>
	)
}
